import React, { useState, useRef, useEffect } from 'react';
import { Send, Sparkles, Menu, X, ArrowRight, User, Star, Zap, Heart, Briefcase } from 'lucide-react';
import { Analytics } from '@vercel/analytics/react';
import { track } from '@vercel/analytics';



const AIBaba = () => {
  const [showChat, setShowChat] = useState(false);
  const [userName, setUserName] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    if (showChat && messages.length === 0) {
      const welcome = userName
        ? `🙏 Namaste ${userName}! Main AI Baba hoon. Aapka cosmic buddy. Aaj kya jaanna hai?`
        : `🙏 Namaste! Main AI Baba hoon. Chalo, aapke cosmic signals decode karte hain. Aapka naam kya hai?`;

      setMessages([{ type: 'bot', text: welcome }]);
    }
  }, [showChat, userName, messages.length]);

  // State for Lead Capture Modal
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadName, setLeadName] = useState('');
  const [leadMobile, setLeadMobile] = useState('');

  const handleStart = () => {
    setShowLeadModal(true);
    track('landing_page_cta_click');
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadName || !leadMobile) return;

    // Strict 10-digit validation
    if (leadMobile.length !== 10) {
      alert("Please enter a valid 10-digit mobile number.");
      return;
    }

    // Save to Database
    try {
      await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: leadName, mobile: leadMobile })
      });
      track('lead_submitted', { name: leadName });
    } catch (err) {
      console.error("Failed to save lead:", err);
      // Proceed solely on frontend even if DB fails
    }

    setUserName(leadName); // Set name for chat
    setShowLeadModal(false);
    setShowChat(true);
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    if (value.length <= 10) {
      setLeadMobile(value);
    }
  };


  const callGeminiAPI = async (userMessage) => {
    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage })
      });
      const data = await response.json();
      return data.reply || data.error || 'Server busy hai, thoda wait karo...';
    } catch (error) {
      console.error('Error:', error);
      return 'Internet ka chakkar hai shayed! Try again.';
    }
  };

  const handleSend = async (messageText = null) => {
    const userMessage = messageText || input.trim();
    if (!userMessage) return;

    // Handle initial name capture if not set (fallback if they bypassed modal somehow)
    if (showChat && !userName && messages.length === 1) {
      setUserName(userMessage);
      setMessages(prev => [...prev,
      { type: 'user', text: userMessage },
      { type: 'bot', text: `Bahut badiya, ${userMessage}! Ab batao, aaj kaisa lag raha hai? Rashifal chahiye ya career ka scene?` }
      ]);
      setInput('');
      return;
    }

    setMessages(prev => [...prev, { type: 'user', text: userMessage }]);
    setInput('');
    setIsTyping(true);
    track('message_sent');

    const response = await callGeminiAPI(userMessage);

    setIsTyping(false);
    setMessages(prev => [...prev, { type: 'bot', text: response }]);
    inputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // --- Landing Page View ---
  if (!showChat) {
    return (
      <div className="min-h-screen relative flex flex-col items-center justify-center p-6 page-transition">

        {/* Lead Capture Modal */}
        {showLeadModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl relative animate-scale-up">
              <button
                onClick={() => setShowLeadModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>

              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="text-purple-600 w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Bas ek step aur!</h3>
                <p className="text-gray-500 mt-2">Apna naam aur number bataiye taaki Baba aapko pehchan sakein.</p>
              </div>

              <form onSubmit={handleLeadSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pura Naam</label>
                  <input
                    type="text"
                    value={leadName}
                    onChange={(e) => setLeadName(e.target.value)}
                    placeholder="Rahul Kumar"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-900 bg-white focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                  <input
                    type="tel"
                    value={leadMobile}
                    onChange={handleMobileChange}
                    placeholder="9876543210"
                    pattern="[0-9]{10}"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-900 bg-white focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all"
                >
                  Kundali Kholo 🔮
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Animated Background Layers */}
        <div className="cosmic-universe">
          <div className="nebula-layer"></div>
          <div className="star-container">
            <div className="stars-small" style={{ top: '10%', left: '20%' }}></div>
            <div className="stars-small" style={{ top: '30%', left: '70%', animationDelay: '1s' }}></div>
            <div className="stars-small" style={{ top: '80%', left: '40%', animationDelay: '2s' }}></div>
            <div className="stars-small" style={{ top: '60%', left: '90%', animationDelay: '0.5s' }}></div>
          </div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-12">
          {/* Left Content */}
          <div className="flex-1 text-center md:text-left space-y-8 animate-slide-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-md">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-xs font-bold uppercase tracking-widest text-purple-200">Aapka Personalized AI Astrologer</span>
            </div>

            <h1 className="text-6xl md:text-8xl font-extrabold leading-tight tracking-tighter">
              <span className="gradient-text">AI Baba</span><br />
              <span className="text-white text-glow text-4xl md:text-6xl">Future Ka Gyan!</span>
            </h1>

            <p className="text-xl md:text-2xl text-purple-200/80 max-w-xl font-light leading-relaxed">
              AI Baba ke saath apne sitaaron ki chaal samjho. Shuddh Hinglish mein baatein karo aur life ki har problem ka solution paao.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start pt-4">
              <button
                onClick={handleStart}
                className="premium-button px-10 py-5 rounded-2xl text-xl font-bold flex items-center justify-center gap-3 group"
              >
                Bhavishya Dekho
                <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
              </button>
            </div>

            {/* Feature Pills */}
            <div className="flex flex-wrap gap-3 pt-6 justify-center md:justify-start">
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl glass-morphism text-sm font-medium">
                <Star className="w-4 h-4 text-yellow-400" /> Daily Rashifal
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl glass-morphism text-sm font-medium">
                <Briefcase className="w-4 h-4 text-blue-400" /> Career Scene
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl glass-morphism text-sm font-medium">
                <Heart className="w-4 h-4 text-pink-400" /> Love Life
              </div>
            </div>
          </div>

          {/* Right Mascot (Code Animated) */}
          <div className="flex-1 flex justify-center items-center relative mascot-static mask-fade">
            <div className="absolute inset-0 bg-purple-600/20 blur-[120px] rounded-full"></div>
            <img
              src="/ai-baba.png"
              alt="AI Baba"
              className="w-[80%] md:w-full max-w-lg relative z-10"
            />
          </div>
        </div>

        {/* Footer info */}
        <div className="absolute bottom-8 left-0 right-0 text-center opacity-40">
          <p className="text-[10px] tracking-widest uppercase font-bold text-purple-200">Powered by Cosmic Intelligence & Gemini AI</p>
        </div>
      </div>
    );
  }

  // --- Chat View (Light Mode) ---
  return (
    <div className="flex flex-col h-screen light-mode page-transition">
      <header className="bg-white/80 backdrop-blur-xl border-b border-gray-100 p-4 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-100 to-indigo-100 p-1.5 shadow-sm">
              <img src="/ai-baba.png" alt="AI Baba" className="w-full h-full object-contain" />
            </div>
            <div>
              <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">AI Baba</h2>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Astrology Genius</span>
              </div>
            </div>
          </div>
          <button onClick={() => setShowMenu(!showMenu)} className="p-2.5 hover:bg-gray-50 rounded-xl transition-colors text-gray-500">
            {showMenu ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {showMenu && (
          <div className="absolute top-full left-0 right-0 bg-white border-b border-gray-100 shadow-2xl p-6 animate-slide-down">
            <div className="max-w-4xl mx-auto space-y-4">
              <button onClick={() => { setMessages([]); setShowMenu(false); }} className="w-full text-left px-5 py-4 hover:bg-gray-50 rounded-2xl flex items-center gap-4 text-gray-700 font-semibold transition-all border border-transparent hover:border-gray-100">
                <Zap className="text-purple-500" /> Nayi Baatchit
              </button>
              <button onClick={() => setShowChat(false)} className="w-full text-left px-5 py-4 hover:bg-gray-50 rounded-2xl flex items-center gap-4 text-gray-700 font-semibold transition-all border border-transparent hover:border-gray-100">
                <User className="text-blue-500" /> Landing Page Par Jao
              </button>
            </div>
          </div>
        )}
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'} animate-slide-up`}>
              <div className={`max-w-[85%] md:max-w-xl ${msg.type === 'bot' ? 'flex gap-4 items-end' : ''}`}>
                {msg.type === 'bot' && (
                  <div className="w-8 h-8 rounded-full bg-purple-50 p-1 flex-shrink-0 shadow-sm">
                    <img src="/ai-baba.png" alt="Baba" className="w-full h-full object-contain" />
                  </div>
                )}
                <div className={`px-6 py-4 rounded-3xl text-base md:text-lg leading-relaxed shadow-sm ${msg.type === 'user'
                  ? 'light-bubble-user rounded-br-none'
                  : 'light-bubble-bot rounded-bl-none'
                  }`}>
                  {msg.text}
                </div>
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="flex gap-4 items-end animate-pulse">
              <div className="w-8 h-8 rounded-full bg-purple-50 p-1">
                <img src="/ai-baba.png" alt="Baba" className="w-full h-full object-contain" />
              </div>
              <div className="bg-gray-50 px-6 py-4 rounded-3xl rounded-bl-none text-sm font-bold text-gray-400 italic">
                Baba soch rahe hain...
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <footer className="bg-white border-t border-gray-100 p-6 sticky bottom-0">
        <div className="max-w-4xl mx-auto flex gap-4">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Poochho jo bhi dil mein hai..."
            className="flex-1 bg-gray-50 border border-gray-100 rounded-2xl px-6 py-4 focus:bg-white focus:border-purple-300 focus:ring-4 focus:ring-purple-500/5 transition-all outline-none text-lg text-gray-800 shadow-inner"
          />
          <button
            onClick={() => handleSend()}
            disabled={isTyping || !input.trim()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white p-4 rounded-2xl transition-all shadow-lg hover:shadow-indigo-500/30 active:scale-95 disabled:opacity-50"
          >
            <Send size={24} />
          </button>
        </div>
      </footer>
      <Analytics />
    </div>
  );
};

export default function App() {
  return <AIBaba />;
}